/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 87, Figma aHMD153Svj9kcw2G1nv1iR "08 · Calls" 09b–09e): the
 * end of a call in the style of the call — the large avatar (a group's for
 * a group call), the name, one line of status ("Звонок завершён · 12:31",
 * "Не отвечает", "Звонок отклонён", "Групповой звонок завершён · 24:05")
 * and two buttons, "Закрыть" and "Перезвонить". It closes by itself after
 * ION_CALL_ENDED_MS; only then (or on "Закрыть") the app is asked to close
 * the call window.
 */

import { type FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  CloseIcon,
  VoiceCallSolidIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";

import { Avatar } from "../Avatar";
import { useAppBarHidden } from "../AppBar";
import { ionAvatarStyle } from "./avatarColor";
import { pick, type IonText } from "./callErrorTexts";
import { durationText } from "./callScreen";
import styles from "./IonCallEndedView.module.css";

// Owner 02.11: 3 s was too short to press "Call back"
export const ION_CALL_ENDED_MS = 6_000;

export const CALL_ENDED_TEXTS = {
  ended: { ru: "Звонок завершён", en: "Call ended" },
  groupEnded: { ru: "Групповой звонок завершён", en: "Group call ended" },
  noAnswer: { ru: "Не отвечает", en: "No answer" },
  declined: { ru: "Звонок отклонён", en: "Call declined" },
  cancelled: { ru: "Звонок отменён", en: "Call cancelled" },
  close: { ru: "Закрыть", en: "Close" },
  callBack: { ru: "Перезвонить", en: "Call back" },
} satisfies Record<string, IonText>;

/** "cancelled": we hung up an outgoing call of two that was not answered. */
export type IonCallEndReason =
  | "timeout"
  | "user"
  | "allOthersLeft"
  | "decline"
  | "cancelled";

/** The status line under the name. */
export function callEndedStatus(
  reason: IonCallEndReason,
  group: boolean,
  seconds: number,
  language: string | undefined,
): string {
  if (reason === "timeout") return pick(CALL_ENDED_TEXTS.noAnswer, language);
  if (reason === "decline") return pick(CALL_ENDED_TEXTS.declined, language);
  if (reason === "cancelled") return pick(CALL_ENDED_TEXTS.cancelled, language);
  const ended = pick(
    group ? CALL_ENDED_TEXTS.groupEnded : CALL_ENDED_TEXTS.ended,
    language,
  );
  return `${ended} · ${durationText(seconds)}`;
}

interface Props {
  /** Whose colour the avatar has (the other person in a call of two, else
   * the room), and the name and avatar to show. */
  id: string;
  name: string;
  avatarUrl: string | undefined;
  group: boolean;
  reason: IonCallEndReason;
  /** How long the call was. */
  seconds: number;
  /** Asks the app to close the call window. */
  onClose: () => void;
  onCallBack: () => void;
  /** For tests. */
  closeAfterMs?: number;
}

export const IonCallEndedView: FC<Props> = ({
  id,
  name,
  avatarUrl,
  group,
  reason,
  seconds,
  onClose,
  onCallBack,
  closeAfterMs = ION_CALL_ENDED_MS,
}) => {
  const { i18n } = useTranslation();
  const l = i18n.language;
  // BUGS 204: no app bar here, so no "minimise" — the screen closes by itself
  useAppBarHidden(true);
  // Close once: by the timer or by the button, whichever is first
  const done = useRef(false);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (done.current) return;
      done.current = true;
      close.current();
    }, closeAfterMs);
    return (): void => clearTimeout(timeout);
  }, [closeAfterMs]);

  return (
    <div className={styles.view} data-testid="ion-call-ended">
      <div className={styles.who}>
        <Avatar
          id={id}
          name={name}
          src={avatarUrl}
          size={128}
          style={ionAvatarStyle(id)}
        />
        <span className={styles.name}>{name}</span>
        <span className={styles.status} data-testid="ion-call-ended-status">
          {callEndedStatus(reason, group, seconds, l)}
        </span>
      </div>
      <div className={styles.actions}>
        <div className={styles.action}>
          <button
            type="button"
            className={styles.close}
            aria-label={pick(CALL_ENDED_TEXTS.close, l)}
            onClick={() => {
              if (done.current) return;
              done.current = true;
              onClose();
            }}
          >
            <CloseIcon width={24} height={24} aria-hidden />
          </button>
          <span className={styles.label}>
            {pick(CALL_ENDED_TEXTS.close, l)}
          </span>
        </div>
        <div className={styles.action}>
          <button
            type="button"
            className={styles.callBack}
            aria-label={pick(CALL_ENDED_TEXTS.callBack, l)}
            onClick={() => {
              if (done.current) return;
              done.current = true;
              onCallBack();
            }}
          >
            <VoiceCallSolidIcon width={24} height={24} aria-hidden />
          </button>
          <span className={styles.label}>
            {pick(CALL_ENDED_TEXTS.callBack, l)}
          </span>
        </div>
      </div>
    </div>
  );
};
